import React, { useState, useEffect, useMemo } from 'react';
import { graphql, Link } from 'gatsby';
import { useAddItemToCart } from 'gatsby-theme-shopify-manager';
import PropTypes from 'prop-types';
import { DialogOverlay, DialogContent } from '@reach/dialog';

import {
  Layout,
  SEO,
  Gallery,
  Accordion,
  RelatedProducts,
  JustLanded,
  JustDropped,
  NewsletterSignup,
  GoogleMap,
  AddToCartAlert,
} from '../components';
import {
  prepareVariantsWithOptions,
  prepareVariantsImages,
  resizeShopifyImage,
} from '../utils';
import { useGraphQL } from '../hooks';

function ProductPageTemplate({ data: { shopifyProduct: product } }) {
  const sizes =
    product.options.find((option) => option.name.toLowerCase() === 'size')
      ?.values || [];

  const tallas =
    product.options.find((option) => option.name.toLowerCase() === 'talla')
      ?.values || [];

  const variants = useMemo(() => prepareVariantsWithOptions(product.variants), [
    product.variants,
  ]);
  const images = useMemo(() => prepareVariantsImages(variants, 'colour'), [
    variants,
  ]);

  const [activeImage, setActiveImage] = useState(0);

  if (images.length < 1) {
    throw new Error('Must have at least one product image!');
  }

  const [isAlertShown, setIsAlertShown] = useState(false);

  const addItemToCart = useAddItemToCart();
  const [variant, setVariant] = useState(variants[0]);
  const [size, setSize] = useState(variant.size);
  const [talla, setTalla] = useState(variant.talla);

  useEffect(() => {
    const newVariant = variants.find((v) => {
      return v.size === size && v.talla === talla;
    });

    if (variant.shopifyId !== newVariant.shopifyId) {
      setVariant(newVariant);
    }
  }, [size, talla, variants, variant.shopifyId]);

  async function handleAddToCart() {
    try {
      await addItemToCart(variant.shopifyId, 1);
      setIsAlertShown(true);
    } catch (e) {
      setIsAlertShown(false);
      console.error(e);
    }
  }

  const { placeholderImage } = useGraphQL();

  const imageSrc = product.images[activeImage]
    ? resizeShopifyImage({
        url: product.images[activeImage]?.originalSrc,
        width: 600,
      })
    : placeholderImage.publicURL;

  return (
    <Layout>
      <SEO
        title={product.title}
        description={product.description}
        type="product"
        image={product.images[activeImage]?.originalSrc}
      />
      <div className="grid gap-6 pt-6">
        <article className="relative w-full px-4 py-6 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <h1 className="sr-only">{product.title}</h1>
          <div className="grid gap-12 sm:grid-cols-2">
            <div>
              <div className="relative h-0 aspect-ratio-square">
                <img
                  src={imageSrc}
                  alt={product.images[activeImage]?.altText}
                  className="absolute inset-0 object-contain w-full h-full"
                />
              </div>
              <Gallery
                images={product.images}
                setActiveImage={setActiveImage}
              />
            </div>
            <div className="space-y-6">
              <div>
                <div className="tracking-wider uppercase">{product.vendor}</div>
                <dl className="flex items-baseline justify-between space-x-4 text-4xl font-medium tracking-wider uppercase">
                  <dt>{product.title}</dt>
                  <dd className="">${variant.priceV2.amount}</dd>
                </dl>
              </div>
              <div className="tracking-wider text-gray-600 uppercase">
                Or 4 payments of $
                {(Number(variant.priceV2.amount) / 4).toFixed(2)} with{' '}
                <span className="font-medium">Afterpay</span>
              </div>
              <div
                dangerouslySetInnerHTML={{ __html: product.descriptionHtml }}
                className="tracking-wider prose text-gray-600 uppercase"
              />
              {product.availableForSale ? (
                <>
                  <OptionPicker
                    name="Size"
                    options={sizes}
                    selected={size}
                    setOption={setSize}
                    variants={variants}
                  />
                  <OptionPicker
                    name="Talla"
                    options={tallas}
                    selected={talla}
                    setOption={setTalla}
                    variants={variants}
                  />
                  <button
                    type="button"
                    onClick={handleAddToCart}
                    disabled={!variant.availableForSale}
                    className={`px-20 uppercase button button-inverted ${
                      variant.availableForSale
                        ? ''
                        : 'opacity-50 cursor-not-allowed hover:bg-white hover:text-dark-pink'
                    }`}
                  >
                    Add to bag
                  </button>
                </>
              ) : (
                <div>This products is currently unavailable.</div>
              )}
              <Accordion heading="Shipping Terms">
                <ShippingTerms />
              </Accordion>
              <Accordion heading="Returns Info">
                <ReturnsInfo />
              </Accordion>
            </div>
          </div>
        </article>
        <RelatedProducts product={product} />
        <JustLanded />
        <JustDropped />
      </div>
      <NewsletterSignup />
      <GoogleMap />
      <AddToCartAlert
        product={product}
        price={variant.priceV2.amount}
        isAlertShown={isAlertShown}
        variant={variant}
        setIsAlertShown={setIsAlertShown}
        image={imageSrc}
      />
    </Layout>
  );
}

ProductPageTemplate.propTypes = {
  data: PropTypes.object.isRequired,
};

function OptionPicker({ name, options, selected, setOption, variants }) {
  if (options < 1) return null;
  return (
    <div>
      <div>{name}</div>
      <div className="flex flex-wrap items-start">
        <div className="mr-4 -mt-3">
          {variants.map((variant) => (
            <button
              key={variant.shopifyId}
              type="button"
              disabled={!variant.availableForSale}
              onClick={() => setOption(variant.size || variant.talla)}
              className={`inline-flex items-center justify-center h-8 mt-3 mr-4 text-sm leading-none whitespace-no-wrap transition duration-150 ease-in-out border rounded-full border-dark-pink last:mr-0
              ${
                (variant.size === selected || variant.talla === selected) &&
                variant.availableForSale
                  ? 'bg-dark-pink text-white'
                  : 'bg-white text-dark-pink'
              }
              ${variant.length < 3 ? 'w-8' : 'w-auto px-4'}
              ${
                !variant.availableForSale ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {variant.size || variant.talla}
            </button>
          ))}
        </div>
        <SizeGuideModal />
      </div>
    </div>
  );
}

OptionPicker.propTypes = {
  name: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  selected: PropTypes.string,
  setOption: PropTypes.func.isRequired,
  variants: PropTypes.array.isRequired,
};

function SizeGuideModal() {
  const [showDialog, setShowDialog] = useState(false);
  const open = () => setShowDialog(true);
  const close = () => setShowDialog(false);
  return (
    <div>
      <button
        type="button"
        onClick={open}
        className="h-8 text-sm tracking-widest underline uppercase"
      >
        Size Guide
      </button>
      <DialogOverlay
        isOpen={showDialog}
        onDismiss={close}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      >
        <DialogContent
          aria-label="Size Guide"
          className="relative max-w-md focus:outline-none focus:shadow-outline"
        >
          <button
            type="button"
            aria-label="Close Size Guide"
            onClick={close}
            className="absolute top-0 right-0 flex items-center justify-center w-12 h-12 transform translate-x-full -translate-y-full rounded-full focus:outline-none focus:bg-dark-pink"
          >
            <svg
              className="w-6 h-6 text-white"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
          <img
            src="https://cdn.shopify.com/s/files/1/2956/0730/files/2019-Moda-Latina-Marketing-Sizing-Chart-V1_large.jpg?v=1552271943"
            alt="Moda Latina Size Guide"
          />
        </DialogContent>
      </DialogOverlay>
    </div>
  );
}

function ShippingTerms() {
  return (
    <div className="prose">
      <p>
        Any purchase made will be filled within 5 business days and mailed by
        registered post to the purchaser, with an expected delivery time of 3
        days. Mailing receipts will be emailed to the customer and Moda Latina
        accepts no responsibility for damage or loss whilst in transit from our
        locations.
      </p>
    </div>
  );
}

function ReturnsInfo() {
  return (
    <div className="prose">
      <p>
        <Link to="/returns-policy/">Returns Policy</Link>
      </p>
    </div>
  );
}

export const ProductPageQuery = graphql`
  query productPage($productId: String!) {
    shopifyProduct(id: { eq: $productId }) {
      availableForSale
      descriptionHtml
      id
      images {
        altText
        originalSrc
      }
      options {
        name
        values
      }
      title
      variants {
        availableForSale
        image {
          altText
          originalSrc
        }
        priceV2 {
          amount
        }
        shopifyId
        selectedOptions {
          name
          value
        }
      }
    }
  }
`;

export default ProductPageTemplate;
